import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BackendFilter } from '../../../interfaces/sessions';

export enum DateOptions {
  TODAY = 'Today',
  YESTERDAY = 'Yesterday',
  THIS_WEEK = 'This week',
  THIS_MONTH = 'This month',
  LAST_WEEK = 'Last week',
  LAST_2_WEEKS = 'Last 2 weeks',
  LAST_MONTH = 'Last month',
  CUSTOM = 'Custom'
}

export enum TimeZoneOptions {
  LOCAL = 'Your timezone',
  PST = 'America/PST',
  MST = 'America/MST',
  CST = 'America/CST',
  EST = 'America/EST',
  GMT = 'London/GMT',
  CEST = 'Paris/CEST'
}

export function roundDateDown(date: Date): Date { return new Date(date.getFullYear(), date.getMonth(), date.getDate()); }
export function roundDateDownWithSeconds(date: Date): Date { 
  const rounded = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  rounded.setSeconds(1); // Start at 00:00:01 instead of 00:00:00
  return rounded;
}
export function roundDateUp(d: Date) {
  const t = new Date(d);
  t.setHours(23, 59, 59, 59);
  return t;
}

export type DateRangeFilter = {
  startDate: Date;
  endDate: Date
}

// Redux-safe version with serializable dates
export type ReduxSafeDateRangeFilter = {
  startDate: string; // ISO string
  endDate: string; // ISO string
}

export interface ComparitiveFilters {
  analytics: ReduxSafeDateRangeFilter & {dateRange: DateOptions};
}

const dateRangeFilterInitialState: ComparitiveFilters = {
  analytics: {
    startDate: roundDateDown(new Date()).toISOString(),
    endDate: roundDateUp(new Date()).toISOString(),
    dateRange: DateOptions.TODAY
  },
}
export const analyticsDateFilterSlice = createSlice({
  name: 'analyticsDateFilter',
  initialState: dateRangeFilterInitialState,
  reducers: {
    updateAnalyticsDateRange: (state, action: PayloadAction<{dateRange: DateOptions, startDate?: string, endDate?: string}>) => {
      state.analytics.dateRange = action.payload.dateRange;
      switch (action.payload.dateRange) {
        case DateOptions.TODAY:
          state.analytics.startDate = roundDateDown(new Date()).toISOString();
          state.analytics.endDate = roundDateUp(new Date()).toISOString();
          break;
        case DateOptions.YESTERDAY:
          state.analytics.startDate = roundDateDown(new Date(new Date().getTime() - 24 * 60 * 60 * 1000)).toISOString();
          state.analytics.endDate = roundDateUp(new Date(new Date().getTime() - 24 * 60 * 60 * 1000)).toISOString(); 
          break;
        case DateOptions.THIS_WEEK:
          state.analytics.startDate = getClosestPrecedingSunday(new Date()).toISOString();
          state.analytics.endDate = roundDateUp(new Date()).toISOString();
          break;
        case DateOptions.THIS_MONTH:
          state.analytics.startDate = roundDateDownWithSeconds(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).toISOString();
          state.analytics.endDate = roundDateUp(new Date()).toISOString();
          break;
        case DateOptions.LAST_WEEK:
          const thisWeekSunday = getClosestPrecedingSunday(new Date());
          const lastWeekSunday = new Date(thisWeekSunday.getTime() - 7 * 24 * 60 * 60 * 1000);
          const lastWeekSaturday = new Date(thisWeekSunday.getTime() - 24 * 60 * 60 * 1000);
          state.analytics.startDate = roundDateDownWithSeconds(lastWeekSunday).toISOString();
          state.analytics.endDate = roundDateUp(lastWeekSaturday).toISOString();
          break;
        case DateOptions.LAST_2_WEEKS:
          state.analytics.startDate = roundDateDownWithSeconds(new Date(new Date().getTime() - 13 * 24 * 60 * 60 * 1000)).toISOString();  
          state.analytics.endDate = roundDateUp(new Date()).toISOString();
          break;
        case DateOptions.LAST_MONTH:
          const today = new Date();
          const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
          state.analytics.startDate = roundDateDownWithSeconds(firstDayLastMonth).toISOString();
          state.analytics.endDate = roundDateUp(lastDayLastMonth).toISOString();
          break;
        case DateOptions.CUSTOM:
          if (action.payload.startDate && action.payload.endDate) {
            state.analytics.startDate = roundDateDown(new Date(action.payload.startDate)).toISOString();
            state.analytics.endDate = roundDateUp(new Date(action.payload.endDate)).toISOString();
          }
          break;
        default:
          break;
      }
    },
  }
})
export const { updateAnalyticsDateRange } = analyticsDateFilterSlice.actions;

export enum TranscriptDateOptions {
  ALL = 'All',
  LAST_WEEK = 'Last week',
  LAST_2_WEEKS = 'Last 2 weeks',
  LAST_30_DAYS = 'Last 30 days',
  CUSTOM = 'Custom'
}

export interface TranscrtiptDateFilter {
  dateRange: ReduxSafeDateRangeFilter
  dateOptions: TranscriptDateOptions
}

export const transcriptDateFilterInitialState: TranscrtiptDateFilter = {
  dateRange: {
    startDate: roundDateDownWithSeconds(new Date(new Date().getTime() - 6 * 24 * 60 * 60 * 1000)).toISOString(),
    endDate: roundDateUp(new Date()).toISOString()
  },
  dateOptions: TranscriptDateOptions.ALL
}

export const transcriptDateFilterSlice = createSlice({
  name: 'transcriptDateFilter',
  initialState: transcriptDateFilterInitialState,
  reducers: {
    updateTranscriptDateFilter: (state, action: PayloadAction<TranscrtiptDateFilter & { originalDateRange?: { startDate: string, endDate: string } }>) => {
      state.dateOptions = action.payload.dateOptions;
      switch (action.payload.dateOptions) {
        case TranscriptDateOptions.LAST_WEEK:
          const transcriptThisWeekSunday = getClosestPrecedingSunday(new Date());
          const transcriptLastWeekSunday = new Date(transcriptThisWeekSunday.getTime() - 7 * 24 * 60 * 60 * 1000);
          const transcriptLastWeekSaturday = new Date(transcriptThisWeekSunday.getTime() - 24 * 60 * 60 * 1000);
          state.dateRange.startDate = roundDateDownWithSeconds(transcriptLastWeekSunday).toISOString();
          state.dateRange.endDate = roundDateUp(transcriptLastWeekSaturday).toISOString(); 
          break;
        case TranscriptDateOptions.LAST_2_WEEKS:
          state.dateRange.startDate = roundDateDownWithSeconds(new Date(new Date().getTime() - 13 * 24 * 60 * 60 * 1000)).toISOString();
          state.dateRange.endDate = roundDateUp(new Date()).toISOString();
          break;
        case TranscriptDateOptions.LAST_30_DAYS:
          state.dateRange.startDate = roundDateDownWithSeconds(new Date(new Date().getTime() - 29 * 24 * 60 * 60 * 1000)).toISOString();
          state.dateRange.endDate = roundDateUp(new Date()).toISOString();
          break;
        case TranscriptDateOptions.CUSTOM:
          if (action.payload.originalDateRange) {
            state.dateRange.startDate = roundDateDown(new Date(action.payload.originalDateRange.startDate)).toISOString();
            state.dateRange.endDate = roundDateUp(new Date(action.payload.originalDateRange.endDate)).toISOString();
          }
          break;
      }
    }
  }
})
export const { updateTranscriptDateFilter } = transcriptDateFilterSlice.actions

export enum FilterContext {
  HISTORY = 'history',
  ANALYTICS = 'analytics'
}

export interface FilterState { value: BackendFilter }
const filterInitialState: FilterState = { value: [] } 
export const analyticsFilterSlice = createSlice({
  name: 'analyticsFilter',
  initialState: filterInitialState,
  reducers: {
    updateAnalyticsFilter: (state, action: PayloadAction<BackendFilter>) => {
      state.value = action.payload.filter((v) => v.length > 0)
    }
  }
})
export const { updateAnalyticsFilter } = analyticsFilterSlice.actions

export const analyticsTranscriptFilterSlice = createSlice({
  name: 'analyticsTranscriptFilter',
  initialState: filterInitialState,
  reducers: { updateAnalyticsTranscriptFilter: (state, action: PayloadAction<BackendFilter>) => { state.value = action.payload } }
})
export const { updateAnalyticsTranscriptFilter } = analyticsTranscriptFilterSlice.actions

// New slice specifically for History filters
export const historyFilterSlice = createSlice({
  name: 'historyFilter', 
  initialState: filterInitialState,
  reducers: {
    updateHistoryFilter: (state, action: PayloadAction<BackendFilter>) => {
      state.value = action.payload.filter((v) => v.length > 0)
    }
  }
})
export const { updateHistoryFilter } = historyFilterSlice.actions

// Helper function to get the closest preceding Sunday
function getClosestPrecedingSunday(date: Date): Date {
  const currentDate = new Date(date);
  const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  if (dayOfWeek === 0) {
    // If today is Sunday, return today
    return roundDateDown(currentDate);
  } else {
    // Otherwise, go back to the most recent Sunday
    const daysBack = dayOfWeek;
    const sunday = new Date(currentDate.getTime() - daysBack * 24 * 60 * 60 * 1000);
    return roundDateDownWithSeconds(sunday);
  }
}